import { useCallback } from "react";
import { trpc } from "lib/trpc";
import { UserAvatarUploadSchema } from "@compass/shared/types";

export interface UpdateAvatarHandlerOptions {
    file: File;
}

export type UpdateAvatarHandler = (args: UpdateAvatarHandlerOptions) => Promise<void>;

export const useUpdateAvatar = (): UpdateAvatarHandler => {
    const queryClient = trpc.useContext();
    const getUploadUrl = trpc.users.uploadAvatar.useMutation();
    const completeUpload = trpc.users.completeAvatarUpload.useMutation();

    const updateAvatar = useCallback(
        async ({ file }: UpdateAvatarHandlerOptions) => {
            const { contentType } = UserAvatarUploadSchema.parse({ contentType: file.type });

            const { fileId, uploadUrl } = await getUploadUrl.mutateAsync({
                contentType,
            });

            const bufferFile = await file.arrayBuffer();

            const result = await fetch(uploadUrl, {
                method: "PUT",
                body: bufferFile,
            });

            if (result.status !== 200) {
                throw new Error("Failed to upload file");
            }

            await completeUpload.mutateAsync(
                { fileId },
                {
                    onSuccess: () => {
                        queryClient.users.me.invalidate();
                        queryClient.social.posts.latest.invalidate();
                    },
                },
            );
        },
        [queryClient, getUploadUrl, completeUpload],
    );

    return updateAvatar;
};
